/**
 * Admin 面板 — 仅 admin 可见
 * 显示:事件流看板(总数 / 24h / by_type / 7d 柱状图 / Top 5 学校专业)
 * MODULE4 开关已删除 — events 始终记录,不再需要 toggle
 */
import { useEffect, useState } from 'react'
import { useAuth } from '../lib/store'
import { getAdminAnalytics, trackEvent, ApiError, translateError, type AdminAnalytics } from '../lib/api'

const ZERO: AdminAnalytics = {
  events_count: 0,
  by_type: { search_school: 0, view_school: 0, view_major: 0, report_include: 0 },
  last_24h: 0, last_7d_daily: [],
  top_schools_7d: [], top_majors_7d: [],
}

export default function AdminPanel() {
  const { user, token } = useAuth()
  const [an, setAn] = useState<AdminAnalytics>(ZERO)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => { if (user?.isAdmin && token) refresh() }, [user, token])

  async function refresh() {
    if (!token) return; setErr(null)
    try {
      const a = await getAdminAnalytics(token)
      setAn(a)
    } catch (e) {
      setErr(e instanceof ApiError ? translateError(e.code) : String((e as Error).message))
    }
  }

  async function testTrack() {
    if (!token) return; setBusy(true); setMsg(null); setErr(null)
    try {
      await trackEvent({ event_type: 'view_school', school_name: '测试大学' })
      setMsg('事件已埋')
      await refresh()
    } catch (e) {
      setErr(e instanceof ApiError ? translateError(e.code) : String((e as Error).message))
    } finally { setBusy(false) }
  }

  if (!user?.isAdmin) return null

  // 7d 每日桶 → 迷你柱状图
  const max7d = Math.max(1, ...an.last_7d_daily.map(r => r.n))
  const eventTypeLabels: Array<[keyof AdminAnalytics['by_type'], string]> = [
    ['search_school', '搜学校'], ['view_school', '查学校'],
    ['view_major', '查专业'], ['report_include', 'AI 报告'],
  ]

  return (
    <div style={{ marginTop: '2.5rem', border: '2px solid transparent', borderImage: 'repeating-linear-gradient(45deg,var(--accent),var(--accent) 4px,transparent 4px,transparent 8px) 2', padding: '1rem', background: 'var(--surface)' }}>
      <div className="serif" style={{ fontSize: 'var(--fs-h3)', fontWeight: 600, marginBottom: '.5rem', display: 'flex', alignItems: 'baseline', gap: '.5rem' }}>
        Admin
        <span style={{ fontSize: 'var(--fs-capt)', fontWeight: 400, color: 'var(--ink3)', fontFamily: 'var(--font-sans)' }}>仅 admin 可见</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem', fontSize: 'var(--fs-body)' }}>
        {/* 1. 测试埋点 + 状态 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' }}>
          <button onClick={testTrack} disabled={busy} className="btn-ghost" style={{ fontSize: 'var(--fs-capt)', padding: '.2rem .5rem' }}>测试埋点</button>
          {msg && <span style={{ fontSize: 'var(--fs-capt)', color: 'var(--ok)' }}>{msg}</span>}
          {err && <span style={{ fontSize: 'var(--fs-capt)', color: 'var(--accent)' }}>{err}</span>}
        </div>

        {/* 2. 事件流总览 */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'baseline' }}>
          <span>events 总数 <span className="mono" style={{ fontWeight: 600 }}>{an.events_count}</span></span>
          <span style={{ color: 'var(--ink3)' }}>·</span>
          <span>近 24h <span className="mono" style={{ fontWeight: 600, color: 'var(--brand)' }}>{an.last_24h}</span></span>
        </div>

        {/* 3. by_type 细分 */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {eventTypeLabels.map(([k, l]) => (
            <span key={k} style={{ fontSize: 'var(--fs-capt)' }}>
              {l} <span className="mono" style={{ color: 'var(--ink2)' }}>{an.by_type[k]}</span>
            </span>
          ))}
        </div>

        {/* 4. 7d 每日桶迷你柱状图 */}
        {an.last_7d_daily.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 32 }} title="近 7d 每日事件数(右为今天)">
            {an.last_7d_daily.map((b, i) => (
              <div key={i} style={{
                width: 16, height: `${Math.max(4, (b.n / max7d) * 100)}%`, minHeight: 4,
                background: i === 6 ? 'var(--brand)' : 'var(--ink3)', borderRadius: 1,
                display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                fontSize: 9, color: 'var(--surface)', fontFamily: 'var(--mono)', paddingBottom: 1,
              }}>{b.n > 0 ? b.n : ''}</div>
            ))}
          </div>
        )}

        {/* 5. Top 5 学校 */}
        {an.top_schools_7d.length > 0 && (
          <div style={{ fontSize: 'var(--fs-capt)' }}>
            <span style={{ color: 'var(--ink3)' }}>近 7d 热门学校:</span>
            <span style={{ marginLeft: '.5rem' }}>
              {an.top_schools_7d.map((s, i) => (
                <span key={s.name} style={{ marginRight: '.75rem' }}>
                  <span className="mono" style={{ color: 'var(--ink3)' }}>#{i + 1}</span> {s.name} <span className="mono" style={{ color: 'var(--brand)' }}>{s.n}</span>
                </span>
              ))}
            </span>
          </div>
        )}

        {/* 6. Top 5 专业 */}
        {an.top_majors_7d.length > 0 && (
          <div style={{ fontSize: 'var(--fs-capt)' }}>
            <span style={{ color: 'var(--ink3)' }}>近 7d 热门专业:</span>
            <span style={{ marginLeft: '.5rem' }}>
              {an.top_majors_7d.map((m, i) => (
                <span key={m.name} style={{ marginRight: '.75rem' }}>
                  <span className="mono" style={{ color: 'var(--ink3)' }}>#{i + 1}</span> {m.name} <span className="mono" style={{ color: 'var(--brand)' }}>{m.n}</span>
                </span>
              ))}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
